import React, { useEffect, useState, useCallback, useRef } from "react";
import AdminPageLayout from "../components/AdminPageLayout";
import RichTextEditor from "../components/rich_text_editor/RichTextEditor";
import ImagePickerModal from "../media_images/ImagePickerModal";
import { useToast } from "../../common/toast/ToastContext";
import {
  fetchContentBlocks, fetchContentBlockById,
  createContentBlock, updateContentBlock, deleteContentBlock,
  fetchPublicContentCategories,
} from "./adminContentService";
import { fetchMediaVideos } from "../media_videos/adminMediaVideoService";
import axios from "axios";

const PUBLIC_BASE = process.env.REACT_APP_BASE_URL;
const searchVocab = (q) =>
  axios.get(`${PUBLIC_BASE}/public/vocabulary`, { params: { search: q, page: 0, size: 10 } })
    .then((r) => ({ status: r.status, data: r.data }))
    .catch(() => ({ status: 500, data: [] }));
const searchPhrases = (q) =>
  axios.get(`${PUBLIC_BASE}/public/phrases`, { params: { search: q, page: 0, size: 10 } })
    .then((r) => ({ status: r.status, data: r.data }))
    .catch(() => ({ status: 500, data: [] }));

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];
const DIFFICULTIES = ["", "Beginner", "Intermediate", "Advanced"];

const emptyForm = {
  title: "", body: "", status: "DRAFT",
  difficulty: "", categoryId: "", youtubeUrl: "",
  videos: [], images: [], vocabularies: [], phrases: [],
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([-\w]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const toSrc = (url) => (!url ? "" : url.startsWith("http") ? url : `${BASE_ROOT}${url}`);

// ── Vocabulary search dropdown ────────────────────────────────────────────────
const VocabSearchDropdown = ({ selected, onAdd }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  const search = (q) => {
    setQuery(q);
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      const res = await searchVocab(q);
      if (res.status === 200) {
        const list = Array.isArray(res.data) ? res.data : res.data.content ?? [];
        setResults(list.filter((v) => !selected.find((s) => s.id === v.id)));
      }
      setLoading(false);
    }, 350);
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        style={inp}
        value={query}
        onChange={(e) => search(e.target.value)}
        placeholder="Search vocabulary…"
      />
      {(results.length > 0 || loading) && (
        <div style={dropdownStyle}>
          {loading && <div style={dropItem}>Searching…</div>}
          {results.map((v) => (
            <div key={v.id} style={dropItem}
              onMouseDown={() => { onAdd(v); setQuery(""); setResults([]); }}>
              <strong>{v.word}</strong>
              {v.partOfSpeech && <span style={{ color: "#888", marginLeft: 6, fontSize: 12 }}>{v.partOfSpeech}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Phrase search dropdown ────────────────────────────────────────────────────
const PhraseSearchDropdown = ({ selected, onAdd }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  const search = (q) => {
    setQuery(q);
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      const res = await searchPhrases(q);
      if (res.status === 200) {
        const list = Array.isArray(res.data) ? res.data : res.data.content ?? [];
        setResults(list.filter((p) => !selected.find((s) => s.id === p.id)));
      }
      setLoading(false);
    }, 350);
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        style={inp}
        value={query}
        onChange={(e) => search(e.target.value)}
        placeholder="Search phrases…"
      />
      {(results.length > 0 || loading) && (
        <div style={dropdownStyle}>
          {loading && <div style={dropItem}>Searching…</div>}
          {results.map((p) => (
            <div key={p.id} style={dropItem}
              onMouseDown={() => { onAdd(p); setQuery(""); setResults([]); }}>
              <strong>{p.title}</strong>
              {p.meaning && <span style={{ color: "#888", marginLeft: 6, fontSize: 12 }}>{p.meaning}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inp = { width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box" };
const dropdownStyle = {
  position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
  border: "1px solid #ddd", borderRadius: 6, zIndex: 100, maxHeight: 220, overflowY: "auto",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
};
const dropItem = { padding: "9px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0f0" };

const AdminContentBlocksPage = () => {
  const { showToast } = useToast();
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [allVideos, setAllVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");

  useEffect(() => {
    fetchPublicContentCategories().then((res) => {
      if (res.status === 200) setCategories(Array.isArray(res.data) ? res.data : res.data.content ?? []);
    });
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    const params = { page, size: 20 };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.categoryId = categoryFilter;
    const res = await fetchContentBlocks(params);
    if (res.status === 200) {
      const d = res.data;
      setItems(Array.isArray(d) ? d : d.content ?? []);
      setTotalPages(Array.isArray(d) ? 1 : d.totalPages ?? 1);
    }
    setLoading(false);
  }, [page, search, statusFilter, categoryFilter]);

  useEffect(() => { if (view === "list") loadItems(); }, [view, loadItems]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setView("form"); };

  const openEdit = async (id) => {
    const res = await fetchContentBlockById(id);
    if (res.status === 200) {
      const d = res.data;
      setForm({
        title: d.title || "",
        body: d.body || "",
        status: d.status || "DRAFT",
        difficulty: d.difficulty || "",
        categoryId: d.category?.id ? String(d.category.id) : "",
        youtubeUrl: d.youtubeUrl || "",
        videos: d.videos || [],
        images: d.images || [],
        vocabularies: d.vocabularies || [],
        phrases: d.phrases || [],
      });
      setEditingId(id);
      setView("form");
    } else {
      showToast("Failed to load content block", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this content block?")) return;
    const res = await deleteContentBlock(id);
    if (res.status === 200 || res.status === 204) { showToast("Deleted", "success"); loadItems(); }
    else showToast(res.message || "Delete failed", "error");
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast("Title is required", "error"); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      body: form.body,
      status: form.status,
      difficulty: form.difficulty || null,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      youtubeUrl: form.youtubeUrl || null,
      videos: form.videos.map((v, i) => ({ mediaVideoId: v.mediaVideoId, videoName: v.videoName, sortOrder: i })),
      images: form.images.map((img, i) => ({ mediaImageId: img.mediaImageId, caption: img.caption || "", sortOrder: i })),
      vocabularyIds: form.vocabularies.map((v) => v.id),
      phraseIds: form.phrases.map((p) => p.id),
    };
    const res = editingId
      ? await updateContentBlock(editingId, payload)
      : await createContentBlock(payload);
    setSaving(false);
    if (res.status === 200 || res.status === 201) {
      showToast(editingId ? "Updated" : "Created", "success");
      setView("list");
    } else {
      showToast(res.message || "Save failed", "error");
    }
  };

  const loadVideos = async () => {
    const res = await fetchMediaVideos({ page: 0, size: 100 });
    if (res.status === 200) setAllVideos(Array.isArray(res.data) ? res.data : res.data.content ?? []);
  };

  const addVideo = (vid) => {
    if (form.videos.find((v) => v.mediaVideoId === vid.id)) return;
    // vid.url comes directly from backend e.g. /api/public/media-videos/1/stream
    const videoUrl = vid.url || (vid.visibility === "PROTECTED"
      ? `/api/client/media-videos/${vid.id}/stream`
      : `/api/public/media-videos/${vid.id}/stream`);
    setForm((f) => ({ ...f, videos: [...f.videos, { mediaVideoId: vid.id, videoName: vid.name, videoUrl, visibility: vid.visibility, sortOrder: f.videos.length }] }));
    setShowVideoPicker(false);
  };

  const removeVideo = (idx) => setForm((f) => ({ ...f, videos: f.videos.filter((_, i) => i !== idx) }));

  const addImage = (url, id) => {
    // url is e.g. /api/public/media-images/4/file — prepend BASE_ROOT for preview
    const previewUrl = url.startsWith("http") ? url : `${BASE_ROOT}${url}`;
    setForm((f) => ({ ...f, images: [...f.images, { mediaImageId: id, imageUrl: url, previewUrl, caption: "", sortOrder: f.images.length }] }));
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const addVocab = (v) => setForm((f) => ({ ...f, vocabularies: [...f.vocabularies, v] }));
  const removeVocab = (id) => setForm((f) => ({ ...f, vocabularies: f.vocabularies.filter((v) => v.id !== id) }));

  const addPhrase = (p) => setForm((f) => ({ ...f, phrases: [...f.phrases, p] }));
  const removePhrase = (id) => setForm((f) => ({ ...f, phrases: f.phrases.filter((p) => p.id !== id) }));

  const label = (text) => <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>{text}</label>;

  // ── Form view ──
  if (view === "form") {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h2>{editingId ? "Edit Content Block" : "New Content Block"}</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setView("list")} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 1000 }}>

          {/* Row 1: Title + Status */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 16 }}>
            <div>
              {label("Title *")}
              <input style={inp} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Content block title" />
            </div>
            <div>
              {label("Status")}
              <select style={inp} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Difficulty + Category */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              {label("Difficulty")}
              <select style={inp} value={form.difficulty} onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}>
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d || "— None —"}</option>)}
              </select>
            </div>
            <div>
              {label("Category")}
              <select style={inp} value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}>
                <option value="">— None —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Row 3: YouTube / Vimeo URL */}
          <div>
            {label("YouTube / Vimeo URL")}
            <input style={inp} value={form.youtubeUrl} onChange={(e) => setForm((f) => ({ ...f, youtubeUrl: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." />
            {getEmbedUrl(form.youtubeUrl) && (
              <div style={{ marginTop: 10, borderRadius: 8, overflow: "hidden", aspectRatio: "16/9", maxWidth: 560 }}>
                <iframe
                  src={getEmbedUrl(form.youtubeUrl)}
                  title="preview"
                  allowFullScreen
                  frameBorder="0"
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              </div>
            )}
          </div>

          {/* Body */}
          <div>
            {label("Body (HTML)")}
            <div style={{ minHeight: 560 }}>
              <RichTextEditor value={form.body} onChange={(val) => setForm((f) => ({ ...f, body: val }))} />
            </div>
          </div>

          {/* Videos */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              {label("Uploaded Videos")}
              <button onClick={() => { loadVideos(); setShowVideoPicker(true); }}
                style={{ padding: "5px 12px", border: "1px solid #019095", color: "#019095", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                + Add Video
              </button>
            </div>
            {form.videos.map((v, i) => (
              <div key={i} style={{ marginBottom: 10, background: "#f5f5f5", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px" }}>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>🎬 {v.videoName}</span>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: v.visibility === "PUBLIC" ? "#e8f5e9" : "#fff3e0", color: v.visibility === "PUBLIC" ? "#2e7d32" : "#e65100" }}>{v.visibility || "PUBLIC"}</span>
                  <button onClick={() => removeVideo(i)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer" }}>✕ Remove</button>
                </div>
                {v.visibility === "PROTECTED" ? (
                  <div style={{ padding: 12, color: "#888", fontSize: 13, background: "#fff8e1" }}>🔒 Protected video — preview not available in admin. Will play for authenticated users.</div>
                ) : v.videoUrl ? (
                  <video
                    src={toSrc(v.videoUrl)}
                    controls
                    style={{ width: "100%", maxHeight: 280, display: "block", background: "#000" }}
                    onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "block"; }}
                  />
                ) : null}
                <div style={{ display: "none", padding: 12, color: "#888", fontSize: 13 }}>Preview unavailable.</div>
              </div>
            ))}
          </div>

          {/* Images */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              {label("Images")}
              <button onClick={() => setShowImagePicker(true)}
                style={{ padding: "5px 12px", border: "1px solid #019095", color: "#019095", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                + Add Image
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {form.images.map((img, i) => {
                const src = img.previewUrl || toSrc(img.imageUrl || img.url);
                console.log(`[image preview #${i}] previewUrl=${img.previewUrl} imageUrl=${img.imageUrl} url=${img.url} → src=${src}`);
                return (
                <div key={i} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 8, width: 180 }}>
                  <img
                    src={src}
                    alt={img.caption}
                    style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 4, marginBottom: 6 }}
                    onError={(e) => console.error(`[image preview #${i}] FAILED to load:`, src, e)}
                  />
                  <input style={{ ...inp, fontSize: 12, marginBottom: 4 }} value={img.caption} placeholder="Caption"
                    onChange={(e) => { const imgs = [...form.images]; imgs[i] = { ...imgs[i], caption: e.target.value }; setForm((f) => ({ ...f, images: imgs })); }} />
                  <button onClick={() => removeImage(i)} style={{ width: "100%", padding: 4, background: "#ffebee", color: "#e53935", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>Remove</button>
                </div>
                );
              })}
            </div>
          </div>

          {/* Vocabulary */}
          <div>
            {label("Vocabulary")}
            <VocabSearchDropdown selected={form.vocabularies} onAdd={addVocab} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {form.vocabularies.map((v) => (
                <span key={v.id} style={chipStyle}>
                  {v.word}
                  {v.partOfSpeech && <span style={{ opacity: 0.7, marginLeft: 4, fontSize: 11 }}>{v.partOfSpeech}</span>}
                  <button onClick={() => removeVocab(v.id)} style={chipRemove}>✕</button>
                </span>
              ))}
            </div>
          </div>

          {/* Phrases */}
          <div>
            {label("Phrases")}
            <PhraseSearchDropdown selected={form.phrases} onAdd={addPhrase} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {form.phrases.map((p) => (
                <span key={p.id} style={{ ...chipStyle, background: "#e8f5e9", color: "#2e7d32", borderColor: "#a5d6a7" }}>
                  {p.title}
                  <button onClick={() => removePhrase(p.id)} style={chipRemove}>✕</button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Video picker modal */}
        {showVideoPicker && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
            onClick={() => setShowVideoPicker(false)}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 520, maxHeight: "75vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <strong>Pick a Video</strong>
                <button onClick={() => setShowVideoPicker(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>×</button>
              </div>
              {allVideos.length === 0 ? <p style={{ color: "#888" }}>No videos found.</p> : allVideos.map((v) => (
                <div key={v.id} onClick={() => addVideo(v)}
                  style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", borderRadius: 6, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                  <span style={{ flex: 1 }}>🎬 {v.name}</span>
                  {v.description && <span style={{ color: "#888", fontSize: 12 }}>{v.description}</span>}
                  <span style={{
                    padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                    background: v.visibility === "PUBLIC" ? "#e8f5e9" : "#fff3e0",
                    color: v.visibility === "PUBLIC" ? "#2e7d32" : "#e65100",
                  }}>{v.visibility}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <ImagePickerModal isOpen={showImagePicker} onClose={() => setShowImagePicker(false)} onSelect={addImage} defaultImageUse="GENERAL" />
      </div>
    );
  }

  // ── List view ──
  return (
    <AdminPageLayout
      title="Content Blocks"
      subtitle="Manage rich HTML content blocks linked to topics."
      actions={
        <div style={{ display: "flex", gap: 8 }}>
          <a href="/admin/content-categories" style={{ padding: "8px 14px", border: "1px solid #019095", color: "#019095", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13, textDecoration: "none", fontWeight: 600 }}>⚙ Categories</a>
          <button className="adm-btn adm-btn-primary" onClick={openCreate}>+ New Content Block</button>
        </div>
      }
    >
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input placeholder="Search…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }} />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
          <option value="">All Status</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? <p className="adm-loading">Loading…</p> : (
        <table className="announcement-table">
          <thead>
            <tr><th>Title</th><th>Difficulty</th><th>Category</th><th>Status</th><th>Videos</th><th>Images</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7">No content blocks found.</td></tr>
            ) : items.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 600 }}>{item.title}</td>
                <td>{item.difficulty || "—"}</td>
                <td>{item.category?.name || "—"}</td>
                <td>
                  <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 12, background: item.status === "ACTIVE" ? "#e8f5e9" : item.status === "DRAFT" ? "#fff8e1" : "#fce4ec", color: item.status === "ACTIVE" ? "#2e7d32" : item.status === "DRAFT" ? "#f57f17" : "#c62828" }}>
                    {item.status}
                  </span>
                </td>
                <td style={{ textAlign: "center" }}>{item.videos?.length || 0}</td>
                <td style={{ textAlign: "center" }}>{item.images?.length || 0}</td>
                <td>
                  <button onClick={() => openEdit(item.id)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 16 }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
          <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </AdminPageLayout>
  );
};

const chipStyle = {
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "4px 10px", borderRadius: 999, fontSize: 13,
  background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9",
};
const chipRemove = {
  background: "none", border: "none", cursor: "pointer",
  color: "inherit", fontSize: 12, padding: 0, marginLeft: 2,
};

export default AdminContentBlocksPage;
